import { useState } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

import { Badge, Button, Callout, EmptyState, buttonClass, inputClass } from '@/components/ui';
import { adminMaterialsQuery } from '@/queries';

/**
 * Materials. The consumables a job eats: ink, film, thread, transfer paper.
 *
 * This screen did not exist, and neither did any way to create one. `items`
 * was written by a single developer script that turns the garment catalogue
 * into stock lines, so there was no route to add a roll of film.
 *
 * That made three built screens describe something that could not be entered:
 * a recipe has nothing to consume, the waste report is empty by construction,
 * and job cost is a garment price with the second half missing. R-243.
 *
 * Garments are not created here. They arrive with the catalogue and carry a
 * variant, and one typed in by hand would be a stock line no product points at.
 */

const CLASSES = [
  'blank', 'thread', 'film', 'ink', 'transfer_paper', 'vinyl', 'packaging', 'other',
] as const;

const CLASS_LABEL: Record<string, string> = {
  blank: 'Blank', thread: 'Thread', film: 'Film', ink: 'Ink',
  transfer_paper: 'Transfer paper', vinyl: 'Vinyl',
  packaging: 'Packaging', other: 'Other',
};

interface Material {
  id: string; code: string; name: string; base_uom: string;
  material_class: string; reorder_point: number; reorder_qty: number; active: boolean;
}

const BLANK = {
  code: '', name: '', base_uom: 'ea', material_class: 'ink',
  reorder_point: '0', reorder_qty: '0',
};

export default function AdminMaterialsPage() {
  const qc = useQueryClient();
  const { materials, uoms } = useSuspenseQuery(adminMaterialsQuery).data;
  const [draft, setDraft] = useState({ ...BLANK });
  const [open, setOpen] = useState(false);
  const [said, setSaid] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/materials', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          ...draft,
          reorder_point: Number(draft.reorder_point),
          reorder_qty: Number(draft.reorder_qty),
        }),
      });
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      if (!res.ok) throw new Error(body?.error?.message ?? 'Could not add it.');
    },
    onSuccess: () => {
      setSaid(null);
      setDraft({ ...BLANK });
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['admin', 'materials'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'recipes'] });
    },
    onError: (e: Error) => setSaid(e.message),
  });

  const toggle = useMutation({
    mutationFn: async (m: Material) => {
      const res = await fetch(`/api/materials/${m.id}`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ active: !m.active }),
      });
      if (!res.ok) throw new Error('Could not change it.');
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'materials'] }),
    onError: (e: Error) => setSaid(e.message),
  });

  const rows = materials as unknown as Material[];

  return (
    <div>
      <div className="mb-[14px]">
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Materials</h1>
        <p className="mt-1 text-[12.5px] text-muted">
          The consumables a job uses: ink, film, thread, transfer paper and
          packaging. Garments come from the catalogue and are not added here.
        </p>
      </div>

      {rows.length === 0 && (
        <Callout tone="warn" title="Nothing can be costed until there are materials" className="mb-5">
          A recipe says what one garment consumes. Until a material exists there
          is nothing to consume, so no recipe can be written, the waste report
          stays empty, and a job cost is only the garment.
        </Callout>
      )}

      {!open ? (
        <button type="button" onClick={() => setOpen(true)} className={buttonClass('primary', 'md', 'mb-6')}>
          Add a material
        </button>
      ) : (
        <form
          className="mb-6 rounded-[10px] border border-line-strong bg-panel p-4"
          onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
        >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label className="block">
              <span className="mb-1 block text-[12.5px] font-semibold">Code</span>
              <input
                value={draft.code}
                onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))}
                placeholder="INK-WHITE-1L"
                className={`${inputClass} w-full font-[family-name:var(--font-mono)]`}
                required
              />
              <span className="mt-1 block text-[12px] text-muted">What a label and a count refer to.</span>
            </label>

            <label className="block">
              <span className="mb-1 block text-[12.5px] font-semibold">Name</span>
              <input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder="White plastisol ink, 1 litre"
                className={`${inputClass} w-full`}
                required
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-[12.5px] font-semibold">Kind</span>
              <select
                value={draft.material_class}
                onChange={(e) => setDraft((d) => ({ ...d, material_class: e.target.value }))}
                className={`${inputClass} w-full`}
              >
                {CLASSES.map((c) => <option key={c} value={c}>{CLASS_LABEL[c]}</option>)}
              </select>
            </label>

            {/* A picker, not a text box. `items.base_uom` is a foreign key to
                `uoms`, so a typed unit is a 500, and a unit outside that table
                is one `receive_goods` cannot convert from a supplier's. */}
            <label className="block">
              <span className="mb-1 block text-[12.5px] font-semibold">Unit</span>
              <select
                value={draft.base_uom}
                onChange={(e) => setDraft((d) => ({ ...d, base_uom: e.target.value }))}
                className={`${inputClass} w-full`}
              >
                {(uoms as Array<{ code: string; name: string }>).map((u) => (
                  <option key={u.code} value={u.code}>{u.code} ({u.name.toLowerCase()})</option>
                ))}
              </select>
              <span className="mt-1 block text-[12px] text-muted">
                Every count and every recipe uses this unit.
              </span>
            </label>

            <label className="block">
              <span className="mb-1 block text-[12.5px] font-semibold">Reorder point</span>
              <input
                value={draft.reorder_point}
                onChange={(e) => setDraft((d) => ({ ...d, reorder_point: e.target.value }))}
                inputMode="decimal"
                className={`${inputClass} w-full font-[family-name:var(--font-mono)]`}
              />
              <span className="mt-1 block text-[12px] text-muted">
                Zero means it never appears on the reorder list.
              </span>
            </label>

            <label className="block">
              <span className="mb-1 block text-[12.5px] font-semibold">Reorder quantity</span>
              <input
                value={draft.reorder_qty}
                onChange={(e) => setDraft((d) => ({ ...d, reorder_qty: e.target.value }))}
                inputMode="decimal"
                className={`${inputClass} w-full font-[family-name:var(--font-mono)]`}
              />
            </label>
          </div>

          <p className="mt-3 max-w-[70ch] text-[12px] text-muted">
            The cost starts at zero and stays there until stock is received
            against a purchase order. A guessed opening cost quietly changes
            every job cost that follows, and a stocktake corrects quantity,
            never cost.
          </p>

          {said && <p className="mt-3 rounded-[10px] border border-line p-3 text-[13px]">{said}</p>}

          <div className="mt-4 flex items-center gap-2">
            <Button type="submit" size="sm" disabled={create.isPending}>
              {create.isPending ? 'Adding...' : 'Add the material'}
            </Button>
            <button type="button" onClick={() => { setOpen(false); setSaid(null); }} className={buttonClass('secondary', 'sm')}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {rows.length === 0 ? (
        <EmptyState
          title="No materials yet"
          body="Add the consumables the studio buys: ink, film, thread, transfer
                paper and packaging. Then a recipe can say how much of each a
                garment uses."
        />
      ) : (
        <div className="overflow-hidden rounded-[6px] border border-line bg-panel">
          {/* `relative` keeps absolute children inside the scroller (tracker #8). */}
          <div className="relative overflow-x-auto">
            <table className="dt w-full min-w-[720px] text-left">
              <thead>
                <tr>
                  <th scope="col">Code</th>
                  <th scope="col">Name</th>
                  <th scope="col">Kind</th>
                  <th scope="col">Unit</th>
                  <th scope="col" className="num">Reorder at</th>
                  <th scope="col">State</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.id}>
                    <td className="font-[family-name:var(--font-mono)] text-[12.5px]">{m.code}</td>
                    <td className="text-[13px]">{m.name}</td>
                    <td className="text-[13px] text-muted">{CLASS_LABEL[m.material_class] ?? m.material_class}</td>
                    <td className="font-[family-name:var(--font-mono)] text-[12.5px] text-muted">{m.base_uom}</td>
                    <td className="num font-[family-name:var(--font-mono)] tabular-nums text-muted">
                      {Number(m.reorder_point) > 0 ? Number(m.reorder_point) : ''}
                    </td>
                    <td><Badge tone={m.active ? 'ok' : 'neutral'}>{m.active ? 'In use' : 'Switched off'}</Badge></td>
                    <td className="whitespace-nowrap">
                      <button
                        type="button"
                        className={buttonClass('secondary', 'sm')}
                        disabled={toggle.isPending}
                        onClick={() => toggle.mutate(m)}
                      >
                        {m.active ? 'Switch off' : 'Switch on'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="mt-5 max-w-[70ch] text-[12.5px] text-muted">
        A material is switched off rather than deleted. Jobs that consumed it
        still point at it, and removing the row would take the record of what
        they used with it.
      </p>
    </div>
  );
}
